import { onBeforeUnmount, provide, Ref, ref } from "@vue/composition-api";
import { CallPrice } from "@scrile/api-provider/dist/api/CallPriceProvider";
import { Call } from "@scrile/api-provider/dist/api/CallsProvider";
import { ErrorDTO } from "@scrile/api-provider/dist/lib/apiErrorHandler";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { NotificationData } from "@/types";
import { EVENTS, PROVIDED_DATA, PROVIDED_DATA_KEY } from "@/const";

export default function usePaidCalls(call: Ref<Call | null>) {
  const showPaidConfirm = ref(false);
  const showRefillBalance = ref(false);
  const callPrice = ref(0);
  let unsubscribe: () => void | undefined;

  const refillBalance = () => {
    showRefillBalance.value = false;
    emitter.emit(EVENTS.BALANCE_REFILL_SHOW);
  };

  const reset = () => {
    callPrice.value = 0;
  };

  const paidCallsFindCallPrice = async (userId: string) => {
    const response = await providers.CallPriceProvider.find({ userIds: [userId] });
    const price = response.find(i => i.userId === userId);
    if (!price) {
      const data: CallPrice = {
        userId,
        perMinutePriceId: "0",
        perMinutePricePackage: null,
      };
      return data;
    }
    return price;
  };

  const paidCallsSubscribeRequestPaymentForCall = (
    onData: (callId: string) => void,
    onError?: (e: ErrorDTO[]) => void
  ) => {
    return providers.PaidCallsProvider.subscribeRequestPaymentForCall(onData, onError);
  };

  const getCallPrice = async (userId: string) => {
    const price = await paidCallsFindCallPrice(userId);
    callPrice.value = price.perMinutePricePackage?.amount ?? 0;
  };

  const init = async () => {
    const paidCallEventListener = await paidCallsSubscribeRequestPaymentForCall(async callId => {
      if (call.value && callId === call.value.id) {
        const notification: NotificationData = {
          text: $t("1 min left! Please refill"),
          position: "top",
          timeout: 60 * 1000,
          onNotificationClick: () => refillBalance(),
        };
        emitter.emit(EVENTS.NOTIFICATION_SHOW, notification);
      }
    });
    unsubscribe = () => paidCallEventListener.unsubscribe();
  };

  init();

  provide<PROVIDED_DATA[PROVIDED_DATA_KEY.PAID_CALL_PRICE]>(PROVIDED_DATA_KEY.PAID_CALL_PRICE, callPrice);

  onBeforeUnmount(() => {
    unsubscribe();
  });

  return {
    showPaidConfirm,
    showRefillBalance,
    callPrice,
    getCallPrice,
    reset,
  };
}
